import {Component, EventEmitter, inject, Injector, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DateRange} from "@angular/material/datepicker";
import {MatDialog} from "@angular/material/dialog";
import {MatPaginator} from "@angular/material/paginator";
import {BasePageComponent} from "@core-components/page-content/base-page.component";
import {TabManagementService} from "@core-services/tab-management.service";
import {PageEventInterface} from "@core/interfaces/page-event.interface";
import {chartColors} from "@features/dashboards/chart-options/chart-colors.model";
import {defaultChartOptions} from "@features/dashboards/chart-options/chart-options.model";
import {ChartOptionsService} from "@features/dashboards/chart-options/chart-options.service";
import {ConsoleContextService} from "@security/console-context.service";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {UtilsService} from "@shared-services/utils.service";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {TableActionItem} from "@ui-kit/webkit/components/crud-table/crud-table.model";
import {
  CustomDateRangeChangeEventModel
} from "@ui-kit/webkit/components/custom-date-filter/custom-date-range-change-event.model";
import {CustomDateRangeModel, TimeFrames} from "@ui-kit/webkit/components/custom-date-filter/custom-date-range.model";
import {ChartConfiguration, ChartData} from "chart.js";
import {BaseChartDirective} from "ng2-charts";
import {lastValueFrom, Subject} from "rxjs";
import {SurveyService} from "../../survey.service";
import {Survey, SurveyStatisticsRequest, SurveySubmissionSummary} from "../../surveys.model";
import {SurveySubmissionViewComponent} from "../survey-submission-view/survey-submission-view.component";

@Component({
  selector: 'app-survey-submissions-list',
  templateUrl: './survey-submissions-list.component.html'
})
export class SurveySubmissionsListComponent extends BasePageComponent implements OnInit, OnDestroy {
  @Input('survey') survey!: Survey;

  private _loader = inject(LoadingService);
  private _clearOnDestroy$ = new Subject<void>();

  protected submissions: SurveySubmissionSummary[] = [];
  protected selectedSubmission: SurveySubmissionSummary | null = null;

  protected loading$ = this._loader.loading$;

  // @ts-ignore
  chartOptions: ChartConfiguration['options'] = JSON.parse(JSON.stringify(defaultChartOptions))!;

  pipelineStats: {
    fullyCompletedApplications: number,
    partiallyCompletedApplications: number,
    chartData: ChartData<'bar' | 'line'>
  } = {
    fullyCompletedApplications: 0,
    partiallyCompletedApplications: 0,
    chartData: {
      labels: [],
      datasets: [
        {data: []}
      ]
    }
  }

  dateRange: DateRange<Date> = new DateRange<Date>(null, null);
  dateFilters: CustomDateRangeModel[] = [
    new CustomDateRangeModel('all_time', 0, TimeFrames.DATE_RANGE, 'All time', false, new DateRange<Date>(new Date(this.offering!.offeringStartDate!), new Date())),
    new CustomDateRangeModel('this_month', 0, TimeFrames.MONTH, 'This month'),
    new CustomDateRangeModel('last_month', 1, TimeFrames.MONTH, 'Last month', true),
  ]

  protected refreshSubmissionDataAction = new EventEmitter<any>();
  protected viewAction = new EventEmitter<any>();

  protected readonly submissionsAdditionalActions: TableActionItem[] = [
    {
      icon: 'refresh',
      label: 'Refresh data',
      actionDataSource: 'None',
      enabled: "Always",
      includeData: false,
      eventEmitter: this.refreshSubmissionDataAction,
      keepSelectedRowOnClick: false
    },
    {
      icon: 'open_in_new',
      label: 'View',
      actionDataSource: 'SelectedRow',
      enabled: "RowSelected",
      includeData: true,
      eventEmitter: this.viewAction,
      keepSelectedRowOnClick: false
    }
  ];

  @ViewChild('paginator') paginator!: MatPaginator;
  @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;

  protected readonly submissionModelType = SurveySubmissionSummary;

  constructor(injector: Injector,
              public utils: UtilsService,
              public dateUtils: DateUtilsService,
              private tabService: TabManagementService,
              private surveyService: SurveyService,
              private context: ConsoleContextService,
              public loader: LoadingService,
              private chartOptionsService: ChartOptionsService,
              private matDialog: MatDialog) {
    super(injector);
    this.chartOptionsService.setTooltips(this.chartOptions!, [], [], [])!;
    this.chartOptionsService.setLegendLabels(this.chartOptions!);

    this.refreshSubmissionDataAction.subscribe((event: any) => {
      this.loadAllData();
    });

    this.viewAction.subscribe((event: any) => {
      this.openViewTab(event);
    });
  }

  ngOnInit() {
  }

  ngOnDestroy(): void {
    this._clearOnDestroy$.next();
  }

  getDateRange(event: CustomDateRangeChangeEventModel) {
    this.dateRange = event.dateRange;
    this.loadAllData();
  }

  loadAllData() {
    this.loadChartData()
      .then(() => this.loadSurveyResponsesList());
  }

  private async loadSurveyResponsesList() {
    const request: SurveyStatisticsRequest = {
      fromDate: this.dateRange.start!,
      toDate: this.dateRange.end!,
      formTemplateCode: this.survey.code!
    }

    try {
      const data = await lastValueFrom(this.surveyService.getAllSubmissionsForSurvey(this.survey.uuid!, request)
        .pipe(this._loader.withLoader(this._clearOnDestroy$)));
      const tableData = data.map(m => new SurveySubmissionSummary(m));
      console.log(tableData);
      this.submissions = tableData;
      console.log(data);
    } catch (err) {
      return console.log(err);
    }
  }

  private async loadChartData(): Promise<void> {
    try {
      const request: SurveyStatisticsRequest = {
        fromDate: this.dateRange.start!,
        toDate: this.dateRange.end!,
        formTemplateCode: this.survey.code!
      }

      const value = await lastValueFrom(this.surveyService.getPipelineChartData(this.survey.uuid!, request)
        .pipe(this._loader.withLoader(this._clearOnDestroy$)));
      const totalResponses = value.chartSeries[0];
      const completeResponses = value.chartSeries[1];

      this.pipelineStats.partiallyCompletedApplications = Object(value.params)['partiallyComplete'];
      this.pipelineStats.fullyCompletedApplications = Object(value.params)['fullyComplete'];
      this.pipelineStats.chartData = {
        labels: totalResponses.chartData.map(m => m.label),
        datasets: [
          {
            label: totalResponses.name,
            data: totalResponses.chartData.map(m_1 => m_1.data),
            backgroundColor: chartColors.BLUE,
            borderColor: chartColors.BLUE,
            borderRadius: 5,
            order: 1
          },
          {
            label: completeResponses.name,
            data: completeResponses.chartData.map(m_1 => m_1.data),
            backgroundColor: chartColors.BRIGHT_PINK,
            borderColor: chartColors.BRIGHT_PINK,
            order: 0,
            type: 'line',
            pointRadius: 5,
            pointBorderColor: chartColors.POINT,
            pointBackgroundColor: chartColors.POINT,
          },
        ]
      };
    } catch (err) {
      console.log(err);
    }
  }

  openViewTab(submission?: SurveySubmissionSummary | null) {
    let payload: PageEventInterface = {
      componentToRegister: SurveySubmissionViewComponent,
      pageName: 'Survey ' + submission?.uuid,
      pageHeader: this.offering!.label,
      data: {
        id: submission?.uuid,
        templateId: this.survey.uuid
      },
      id: this.utils.generateUuidWithPrefix('survey-submission-view'),
      offeringId: this.offering!.offeringUuid,
      sectionCode: 'leads',
      pageCode: 'survey-submission-view',
      offering: this.offering
    }
    this.tabService.sendPageAddEvent(payload);
  }

  protected submissionRowSelected($event: any) {
    const hasData: boolean = $event ?? false;
    this.selectedSubmission = hasData ? new SurveySubmissionSummary($event) : null;
  }
}
